import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Mail, QrCode, MousePointerClick, Star, Sparkles } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-xl bg-primary text-white grid place-items-center font-display font-bold">J</div>
            <span className="font-display font-bold text-lg">Job Done Reviews</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="landing-login-btn">Log in</Button></Link>
            <Link to="/signup"><Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="landing-signup-btn">Start free trial</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-primary px-3 py-1 text-xs font-semibold mb-6" data-testid="landing-eyebrow">
            <Sparkles className="h-3.5 w-3.5" /> Made for local service businesses
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900" data-testid="landing-headline">
            Get more Google reviews.<br/><span className="text-primary">Automatically.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl" data-testid="landing-subhead">
            Send every customer a polished review request after the job, hand out a printable QR code,
            and see who actually clicked. Zero fluff, obvious in 5 seconds.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/signup"><Button size="lg" className="rounded-full h-12 px-7 text-base bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="landing-hero-cta">Start free trial</Button></Link>
            <a href="#pricing"><Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base w-full sm:w-auto" data-testid="landing-see-pricing">See pricing</Button></a>
          </div>
          <p className="mt-4 text-sm text-slate-500" data-testid="landing-trust">14-day free trial · No credit card · Cancel anytime</p>
        </div>

        {/* Placeholder screenshot */}
        <div className="relative">
          <div className="mock-screenshot aspect-[4/3] p-6 md:p-8">
            <div className="bg-white rounded-lg border border-slate-200 h-full p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Last 30 days</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-primary font-medium">Live</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-2xl font-display font-bold text-slate-900">128</div><div className="text-xs text-slate-500">Sent</div></div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-2xl font-display font-bold text-slate-900">96</div><div className="text-xs text-slate-500">Opened</div></div>
                <div className="rounded-lg bg-primary/10 border border-primary/30 p-3"><div className="text-2xl font-display font-bold text-primary">37</div><div className="text-xs text-primary/80">Clicked</div></div>
              </div>
              <div className="space-y-2">
                {[
                  { n: "Maria G.", t: "clicked your review link", when: "2h ago" },
                  { n: "James R.", t: "opened email", when: "5h ago" },
                  { n: "Priya S.", t: "clicked your review link", when: "1d ago" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm rounded-lg bg-slate-50/70 px-3 py-2.5">
                    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-bold">{r.n[0]}</div>
                    <div className="flex-1 truncate"><span className="font-medium text-slate-900">{r.n}</span> <span className="text-slate-500">— {r.t}</span></div>
                    <span className="text-xs text-slate-400">{r.when}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Everything you need. Nothing you don't.</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Three simple tools that turn happy customers into real Google reviews.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Mail, title: "Automated review request emails", desc: "One polished email sent after every job — with your logo, your name, and the direct Google review link." },
            { icon: QrCode, title: "Printable QR code", desc: "Get a crisp A6 card PDF and a PNG. Stick it on invoices, work vans, or the counter." },
            { icon: MousePointerClick, title: "See who clicked", desc: "Track opens and clicks per customer so you know who followed through — no guessing." },
          ].map((f, i) => (
            <Card key={i} className="p-8 rounded-2xl border-slate-200 card-hover fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`landing-feature-${i}`}>
              <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center mb-5">
                <f.icon className="h-6 w-6" strokeWidth={2.2} />
              </div>
              <h3 className="font-display font-bold text-lg mb-2 text-slate-900">{f.title}</h3>
              <p className="text-slate-600 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-50 py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-900">One simple price</h2>
          <p className="text-slate-600 mt-3">No tiers, no add-ons, no surprises.</p>

          <Card className="mt-10 p-8 md:p-10 rounded-3xl border-2 border-primary/20 shadow-xl bg-white" data-testid="landing-pricing-card">
            <div className="flex items-center justify-center gap-1 text-yellow-500 mb-2">
              {[0,1,2,3,4].map(i => <Star key={i} className="h-4 w-4 fill-yellow-400 stroke-yellow-400" />)}
            </div>
            <div className="font-display font-extrabold text-6xl text-slate-900">
              $29<span className="text-2xl text-slate-400 font-semibold">/month</span>
            </div>
            <p className="text-slate-600 mt-2">14-day free trial · No credit card · Cancel anytime</p>
            <ul className="mt-8 space-y-3 text-left max-w-md mx-auto">
              {[
                "Unlimited review request emails",
                "Printable QR code card (A6 PDF + PNG)",
                "Open & click tracking",
                "Automatic 3-day reminder (optional)",
                "CSV customer import",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-none" />
                  <span className="text-slate-800">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup"><Button size="lg" className="mt-10 rounded-full h-12 px-8 text-base bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="landing-pricing-cta">Start free trial</Button></Link>
          </Card>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-sm text-slate-500 flex flex-col md:flex-row items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Job Done Reviews</div>
          <div className="flex gap-6">
            <Link to="/login" className="hover:text-slate-800">Log in</Link>
            <Link to="/signup" className="hover:text-slate-800">Start free trial</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
